/**
 * The one place the UI touches the reducer. State goes in, actions go
 * through `step`, and the result of each step comes back so the sheet can
 * say what just happened. Nothing else in `/ui` may change state.
 *
 * The authoritative state lives in a ref and is stepped synchronously: a
 * joystick fires faster than React re-renders, and every nudge must see
 * the one before it. React state mirrors the ref for rendering.
 */

import { type Action, step, type StepResult, type WorldState } from '@sim/index.ts';
import { useCallback, useRef, useState } from 'react';

export interface WorldHandle {
  readonly state: WorldState;
  /** Apply an action. The result carries the new state and the report. */
  readonly dispatch: (action: Action) => StepResult;
}

export function useWorld(initial: WorldState): WorldHandle {
  const current = useRef(initial);
  const [state, setState] = useState(initial);

  const dispatch = useCallback((action: Action): StepResult => {
    const result = step(current.current, action);
    current.current = result.state;
    setState(result.state);
    return result;
  }, []);

  return { state, dispatch };
}
